<template>
  <Modal
    data-test-id="modal-link-protocol"
    is-open
    :title="
      $tc('protocol.linking.link_protocol', undefined, {
        field: element.label,
        target: targetProtocolType?.name || $tc('unknown'),
      })
    "
    :max-width="1200"
    view-only
    @cancel="$emit('cancel')"
  >
    <ProtocolTable
      id="dataTable"
      class="ma-0"
      :protocols="linkableProtocols"
      :is-loading="linkableProtocolsLoading"
      :loading-message="$tc('loading', undefined, { entity: $tc('protocol.linking.linkable_protocols') })"
      :search-bar-title="$tc('protocol.linking.choose_linked_protocol')"
      start-with-all-dates
      hide-deleted
      scroll-target=".v-dialog"
      @protocol-clicked="linkProtocol"
    >
      <template #componentBeforeTableStart>
        <div class="flex justify-center items-center h-13.5">
          <button
            v-t="'protocol.proceed_without_link'"
            class="text-primary text-sm font-semibold border-solid border-1 border-primary rounded h-10.5 px-1 py-2.5"
            data-test="button-createWithoutLink"
            @click="linkProtocol(undefined)"
          />
        </div>
      </template>
    </ProtocolTable>
  </Modal>
</template>

<script lang="ts">
import {
  Element,
  getElementValue,
  getHistoricProtocolContentType,
  isLinkableValue,
  isProtocolLink,
  Protocol,
  ProtocolLink,
} from '@anschuetz-elog/common';
import { computed, defineComponent, PropType, toRef } from 'vue';

import Modal from '#/components/Modal.vue';
import ProtocolTable from '#/components/protocol/ProtocolTable.vue';
import useFeathers from '#/compositions/useFeathers';
import { getHistoricProtocolTypeIds, loadProtocols } from '#/compositions/useProtocols';
import { getLatestObjectsWithHistory } from '#/lib/history';

import { ProtocolLinkConfigProp } from './utils';

export default defineComponent({
  name: 'ProtocolLinkContainer',

  components: {
    Modal,
    ProtocolTable,
  },

  props: {
    element: {
      type: Object as PropType<Element>,
      required: true,
    },

    link: {
      type: Object as PropType<ProtocolLinkConfigProp>,
      required: true,
    },
  },

  emits: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    link: (_link: ProtocolLink | undefined) => true,
    cancel: () => true,
  },

  setup(props, { emit }) {
    const element = toRef(props, 'element');
    const link = toRef(props, 'link');

    const feathers = useFeathers();
    const localCache = feathers.get('localCache');

    const targetProtocolContentType = computed(() =>
      localCache.getOptionalProtocolContentType(link.value.target.protocolContentType._id),
    );

    const currentLinkOwner = computed(() => {
      const {
        source: { protocol },
      } = link.value;
      return {
        _id: protocol._id,
        type: '_id' in protocol.type ? protocol.type._id : protocol.type.ref._id,
      };
    });

    const potentialOtherOwnerTypeIds = getHistoricProtocolTypeIds(computed(() => currentLinkOwner.value?.type));
    const { data: potentialOtherOwners } = loadProtocols(
      potentialOtherOwnerTypeIds,
      computed(() => ({
        _id: { $ne: currentLinkOwner.value?._id },
      })),
    );

    const latestPotentialOwners = computed(() => {
      const protocols = potentialOtherOwners.value.filter((protocol) => {
        const protocolTypeId = '_id' in protocol.type ? protocol.type._id : protocol.type.ref._id;
        // this filtering is redundant (see query of #protocolsWithLinkQuery) but necessary for online only mode
        // TODO: remove this filtering when gateway supports querying for it
        return potentialOtherOwnerTypeIds.value?.includes(protocolTypeId);
      });
      return getLatestObjectsWithHistory(protocols).map((protocolWithHistory) => protocolWithHistory.data);
    });
    const alreadyLinkedProtocolIds = computed(() => {
      const {
        source: { protocolContentType },
      } = link.value;
      return latestPotentialOwners.value.reduce<string[]>((ids, protocol) => {
        const contentType = getHistoricProtocolContentType(feathers, protocol, protocolContentType);
        if (!contentType) {
          return ids;
        }
        let elementValue = getElementValue(protocol, contentType, element.value._id);
        if (!isProtocolLink(elementValue)) {
          return ids;
        }
        return [...ids, elementValue.protocol._id];
      }, []);
    });

    const targetProtocolType = computed(() => {
      return localCache.getOptionalProtocolType(link.value.target.protocolType._id);
    });

    const { data: allLinkableProtocols, isLoading: linkableProtocolsLoading } = loadProtocols(
      getHistoricProtocolTypeIds(targetProtocolType),
    );

    const linkableProtocols = computed(() => {
      const latestProtocols = getLatestObjectsWithHistory(allLinkableProtocols.value).map(
        (protocolWithHistory) => protocolWithHistory.data,
      );
      if (link.value.source.allowedMultipleLinking) {
        return latestProtocols;
      }
      return latestProtocols.filter(
        (linkableProtocol) => !alreadyLinkedProtocolIds.value.includes(linkableProtocol._id),
      );
    });

    function linkProtocol(selectedProtocol: Protocol | undefined): void {
      emit('cancel');
      if (!selectedProtocol || !targetProtocolContentType.value) {
        emit('link', undefined);
        return;
      }
      const contentType = getHistoricProtocolContentType(feathers, selectedProtocol, targetProtocolContentType.value);
      const value = !contentType
        ? undefined
        : getElementValue(selectedProtocol, contentType, link.value.target.elementId);
      if (!isLinkableValue(value)) {
        throw new Error(`Can not link to such a type of value: ${value}`);
      }
      emit('link', {
        protocol: Protocol.createRef(selectedProtocol._id),
        value: isProtocolLink(value) ? value.value : value,
      });
    }

    return {
      allLinkableProtocols,
      linkableProtocols,
      linkableProtocolsLoading,
      linkProtocol,
      alreadyLinkedProtocolIds,
      latestPotentialOwners,
      potentialOtherOwners,
      targetProtocolType,
      currentLinkOwner,
    };
  },
});
</script>
